import { MaxLengthValidatorDirective } from '../../../../shared/directives/max-length-validator.directive';
import { BusinessClock } from '../../../../core/time/business-clock.service';
import { clockView } from '../../../../core/time/business-time';
import { businessDayjs, calendarNow, jstCalendarPlugin } from '../../../../core/time/business-calendar';
import { RegistrationApi } from '../../data-access/registration-api.service';
import { RegistrationQuoteState } from '../../application/registration-quote.service';
import { RegistrationWorkflow } from '../../application/registration-workflow.service';
import { confirmationInput } from '../../domain/registration-input';
import { Component, OnInit, OnDestroy, inject, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import googleCalendarPlugin from '@fullcalendar/google-calendar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ja';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { SfappService } from '../../../../core/services/sfapp.service';
import { MemberStateService } from '../../application/member-state.service';
import { GmoService } from '../../../../core/services/gmo.service';
import { ConvertAlphanumericDirective } from '../../../../shared/directives/convert-alphanumeric.directive';
import {
  getYearList, getCcYearList, getMonthList, getDateList,
  SEX_LIST, NATIONALITY_LIST, PAYMENT_METHOD_LIST, RELATIONSHIP_LIST,
  getDesiredStartDateList, collectOptions, normalizeQuestionaries, isEligibleAge,
} from '../../../../shared/utils/member-helpers';
import { environment } from '../../../../../environments/environment';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ja');

@Component({
    selector: 'app-step2',
    providers: [RegistrationQuoteState],
    imports: [MaxLengthValidatorDirective, CommonModule, FormsModule, TranslatePipe, ConvertAlphanumericDirective, FullCalendarModule],
    templateUrl: './step2.component.html'
})
export class Step2Component implements OnInit, OnDestroy {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(true);

  @ViewChild('stepTwoForm') stepTwoForm?: NgForm;
  readonly quote = inject(RegistrationQuoteState);
  private registrationApi = inject(RegistrationApi);
  private workflow = inject(RegistrationWorkflow);
  private alive = true;
  confirming = false;
  confirmationError = "";
  private sfapp = inject(SfappService);
  private stateService = inject(MemberStateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private toastr = inject(ToastrService);
  private gmo = inject(GmoService);
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);

  state = this.stateService.get();
  get member() { return this.state.member; }

  // Lists
  birthYears: number[] = [];
  birthMonths = getMonthList();
  birthDates = getDateList();
  sexes = SEX_LIST;
  nationalities = NATIONALITY_LIST;
  paymentMethods = PAYMENT_METHOD_LIST;
  ccExpireMonths = getMonthList();
  ccExpireYears: string[] = [];
  relationships = RELATIONSHIP_LIST;
  desiredStartDateList: Array<{ id: string; name: string }> = [];
  plans: any[] = [];
  visitMenu: any[] = [];
  questionarys: any = {};
  questionaryMaster: any[] = [];
  questionaryLoading = false;
  questionaryLoaded = false;
  questionaryError = '';
  get requiresQuestionary(): boolean {
    return (!this.member.isRegistration && !!this.member.isEntry) || this.member.status === 6;
  }

  // Form state
  selectPlan = '';
  selectVisitPlan = '';
  paymentMethod = '';
  selectSex: any = null;
  isJapanese: any = null;
  emRelationship: any = null;
  invalidEmail = false;
  gmoTokenError = false;
  visitDatetime = '';
  visitNote = '';
  visitorsError = false;
  isUnmanned = 0;
  isDisplayedTourPlan = 0;
  isDisplayedCcLink = 0;
  btPaymentEnabled = 0;
  isOKCcName = '';
  isValidBirthDay: boolean | undefined;
  schedules: any[] = [];
  isReserveProcessing = false;
  reservationGlobalFlag = false;
  showVisitModal = false;
  ccUrl = environment.ccUrl;
  lang = 'ja';

  // Agreements
  agreementHtml: Record<string, SafeHtml> = {};

  // FullCalendar options
  calendarOptions: CalendarOptions = {};
  showCalendar = false;

  private sessionTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SESSION_TIMEOUT = 60 * 60 * 1000;

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.params;
    if (params['memberId'] && params['tempPass'] &&
        this.workflow.beginMainRegistration(Number(params['memberId']), Number(params['studioId']))) {
      this.state = this.stateService.get();
    }
    const completed = this.workflow.resume() ?? this.stateService.completedRegistration;
    if (completed) {
      await this.router.navigate(['/step4', completed.type, completed.studioId], { replaceUrl: true });
      return;
    }
    if (this.workflow.message) { this.confirmationError = this.workflow.message; return; }
    this.resetSessionTimer();
    this.lang = this.member.translate ?? this.translate.getCurrentLang() ?? 'ja';
    this.translate.use(this.lang);

    const queryParams = this.route.snapshot.queryParams;

    // Detect mode from URL
    const url = this.router.url;
    // A replacement registration credential owns a fresh draft, even for the same member.
    // Compare the existing short-lived credential without copying it into an owner key.
    this.stateService.enterTarget(`${url.includes('/register/') ? 'register' : url.includes('/visitors/') ? 'visitors' : url.includes('/in_studio/') ? 'in_studio' : 'new'}/${params['studioId'] ?? this.member.studioId}/${params['memberId'] ?? ''}`,
      !!params['tempPass'] && this.member.tmp.tempPass !== params['tempPass']);
    this.state = this.stateService.get();
    this.member.visitors = url.includes('/visitors/');
    this.stateService.setVisitors(this.member.visitors);
    this.member.isRegistration = url.includes('/register/');
    this.stateService.setInStudio(url.includes('/in_studio/'));

    // Handle URL params (direct navigation)
    if (params['studioId']) {
      this.member.studioId = Number(params['studioId']);
      const exists = await this.sfapp.studioExists(params['studioId'], params['studioName']);
      if (!this.alive) return;
      if (!exists) { this.router.navigate(['/step1']); return; }
      this.member.tmp.studio = [params['studioId'], params['studioName']];
    }

    if (!this.member.studioId) { this.router.navigate(['/step1']); return; }

    // Temp member pre-fill (register flow)
    if (this.member.isRegistration && params['memberId'] && Object.keys(this.member.tmp).length <= 2) {
      const data = await this.sfapp.getTempMember(params['memberId'], params['tempPass']);
      if (!this.alive) return;
      if (!data?.result) {
        this.router.navigate(['/step1']);
        return;
      }

      this.member.tmp.tempPass = params['tempPass'];
      this.member.id = Number(params['memberId']);
      Object.assign(this.member, data.result);
      this.selectSex = this.sexes.find((v) => v.id === data.result['sex']) ?? null;
      const zip = data.result['zip']?.split('-');
      if (zip) { this.member.tmp.zip1 = zip[0]; this.member.tmp.zip2 = zip[1]; }
      const birth = data.result['birthday']?.split('-');
      if (birth) {
        this.member.tmp.birth_year = Number(birth[0]);
        this.member.tmp.birth_month = Number(birth[1]);
        this.member.tmp.birth_date = Number(birth[2]);
      }
      this.isJapanese = this.nationalities.find((v) => v.id === data.result['isJapanese']) ?? null;
      const phone = data.result['phone']?.split('-');
      if (phone) { this.member.tmp.tel1 = phone[0]; this.member.tmp.tel2 = phone[1]; this.member.tmp.tel3 = phone[2]; }
    }

    // Restore saved form state
    this.member.tmp.firstInvoiceTotal = undefined;
    this.member.tmp.firstInvoice = undefined;
    if (this.member.tmp.planString) this.selectPlan = this.member.tmp.planString;
    if (this.member.tmp.sex) this.selectSex = this.member.tmp.sex;
    if (this.member.tmp.nationality) this.isJapanese = this.member.tmp.nationality;
    const savedPaymentMethod = this.member.payment.paymentMethod;
    if (savedPaymentMethod !== undefined && savedPaymentMethod !== null && savedPaymentMethod !== '') {
      this.paymentMethod = String(savedPaymentMethod);
    }
    if (this.member.tmp.relationship) this.emRelationship = this.member.tmp.relationship;
    if (this.member.tmp.selectVisitPlan) this.selectVisitPlan = this.member.tmp.selectVisitPlan;
    if (this.member.tmp.visitDatetime) this.member.tmp.visitDatetime = undefined;
    if (this.member.tmp.visitNote) this.visitNote = this.member.tmp.visitNote;
    if (this.member.isEntry === undefined) this.member.isEntry = 1;
    this.isOKCcName = this.member.tmp?.isOKCcName ?? '';

    // Clear expired reservations
    const lsReservationIds: number[] = JSON.parse(localStorage.getItem('reservationId') ?? '[]');
    if (!this.member.reservationId) this.member.reservationId = [];
    if (this.member.reservationId.length || lsReservationIds.length) {
      await this.clearReserve();
      if (!this.alive) return;
    }

    // Load dates and plans
    const [dateData, plans, visitMenuData, reserveGlobal] = await Promise.all([
      this.sfapp.getDate(),
      this.sfapp.getStudioPlanList(this.member.studioId!),
      this.sfapp.getVisitMenu(this.member.studioId!),
      this.sfapp.getReservationGlobalFlag(),
    ]);
    if (!this.alive) return;

    if (dateData) {
      this.desiredStartDateList = getDesiredStartDateList(dateData.arr, dateData.weekday);
      if (this.member.desiredStartDate) {
        const savedDateId = this.member.desiredStartDate.id;
        this.member.desiredStartDate = this.desiredStartDateList.find(date => date.id === savedDateId);
      }
      this.birthYears = getYearList(Number(dateData.arr[0]));
      this.ccExpireYears = getCcYearList(Number(dateData.arr[0]));
    }
    this.plans = plans ?? [];
    if (!this.plans.length) { this.router.navigate(['/step1']); return; }
    this.visitMenu = visitMenuData ?? [];
    this.reservationGlobalFlag = !!reserveGlobal;

    const studioInfo = await this.sfapp.isDisplayed(this.member.studioId!);
    if (!this.alive) return;
    this.isUnmanned = studioInfo?.isUnmanned ?? 0;
    if (this.isUnmanned === 1 && !this.paymentMethod && !this.member.visitors && (this.member.isEntry || this.member.isEntry === undefined)) {
      this.paymentMethod = '1';
    }
    this.isDisplayedTourPlan = studioInfo?.isDisplayedTourPlan ?? 0;
    this.isDisplayedCcLink = studioInfo?.isDisplayedCcLink ?? 0;
    this.btPaymentEnabled = Number(studioInfo?.btPaymentEnabled ?? 0);
    this.member.btPaymentEnabled = this.btPaymentEnabled;
    this.member.tmp.btPaymentEnabled = this.btPaymentEnabled;
    const rawNote = studioInfo?.note?.replace(/\r?\n/g, '<br>') ?? '';
    this.member.tmp.note = rawNote;

    if (queryParams['is_visitors'] === 'true') {
      this.switchToVisit();
      if (!this.isDisplayedTourPlan || !this.visitMenu.length) {
        this.visitorsError = true;
      }
    }

    // Questionnaire
    if (this.requiresQuestionary) await this.loadQuestionary();
    else this.questionaryLoaded = true;

    // Load agreements
    await this.loadAgreements();
  }

  async loadQuestionary(): Promise<void> {
    if (this.questionaryLoading) return;
    this.questionaryLoading = true;
    this.questionaryLoaded = false;
    this.questionaryError = '';
    try {
      this.questionaryMaster = await this.sfapp.getQuestionary();
      this.questionarys = this.member.tmp.questionarys ?? collectOptions(this.questionaryMaster, this.lang);
      normalizeQuestionaries(this.questionarys);
      this.questionaryLoaded = true;
    } catch {
      this.questionaryError = 'アンケートを取得できませんでした。再取得してからお進みください。';
    } finally {
      this.questionaryLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.alive = false; this.quote.clear();
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    // modalを開いたまま離脱した場合にbodyのスクロールが止まったままにならないようにする
    document.body.classList.remove('modal-open');
  }

  private resetSessionTimer(): void {
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    this.sessionTimer = setTimeout(() => {
      alert('セッションがタイムアウトしました。ページをリロードしてください。');
      window.location.reload();
    }, this.SESSION_TIMEOUT);
  }

  private async loadAgreements(): Promise<void> {
    const langs = ['ja', 'en', 'zh-CN'];
    const docs = ['agreement', 'rule', 'pi-agreement'];
    const tasks: Promise<void>[] = [];
    for (const doc of docs) {
      for (const l of langs) {
        const suffix = l === 'ja' ? '' : `-${l.toLowerCase()}`;
        const key = `${doc}-${l}`;
        const url = `assets/agreements/member-${doc}${suffix}.html`;
        tasks.push(
          firstValueFrom(this.http.get(url, { responseType: 'text' }))
            .then((html) => { this.agreementHtml[key] = this.sanitizer.bypassSecurityTrustHtml(html); })
            .catch(() => { this.agreementHtml[key] = ''; })
        );
      }
    }
    await Promise.all(tasks);
  }

  private async clearReserve(): Promise<void> {
    this.visitDatetime = '';
    const tempIds = this.stateService.clearReservations();
    if (tempIds.length) await this.sfapp.removeTempReserve(tempIds);
  }

  changeLanguage(langKey: string): void {
    this.lang = langKey;
    this.translate.use(langKey);
    this.member.translate = langKey;
    this.stateService.setLang(langKey);
    this.member.tmp.questionarys = undefined;
    this.questionarys = collectOptions(this.questionaryMaster, langKey);
    normalizeQuestionaries(this.questionarys);
  }

  isEligibleAge(year?: number, month?: number, day?: number): boolean {
    if (!year || !month || !day) return true;
    const eligible = isEligibleAge(year, month, day);
    this.isValidBirthDay = eligible;
    return eligible;
  }

  onSelectPlanChange(): void {
    this.clearFirstInvoice();
    if (!this.selectPlan || this.selectPlan === '999999999,見学予約,0,0') {
      this.member.desiredStartDate = undefined;
      this.clearFirstInvoice();
    }
    if (this.selectPlan === '999999999,見学予約,0,0') {
      this.paymentMethod = '0';
    }
  }

  onSelectVisitPlanChange(): void {
    if (this.member.reservationId?.length || JSON.parse(localStorage.getItem('reservationId') ?? '[]').length) {
      this.clearReserve();
    }
  }

  onPaymentMethodChange(): void {
    this.clearFirstInvoice();
    if (this.paymentMethod === '0') {
      this.member.desiredStartDate = undefined;
      this.clearFirstInvoice();
    }
  }

  onDesiredStartDateChange(): void {
    void this.loadFirstInvoice();
  }

  async loadFirstInvoice(): Promise<boolean> {
    if (!this.member.desiredStartDate) {
      this.clearFirstInvoice(); delete this.member.isKeyDelivered; return true;
    }
    const planId = this.selectPlan ? Number(this.selectPlan.split(',')[0]) : this.member.studioPlanId;
    if (!planId) return false;
    const date = this.member.desiredStartDate.id;
    const payment = this.paymentMethod;
    const value = await this.quote.load(planId, date, payment);
    if (!this.alive || !value || this.member.desiredStartDate?.id !== date || this.paymentMethod !== payment ||
      (this.selectPlan ? Number(this.selectPlan.split(',')[0]) : this.member.studioPlanId) !== planId) return false;
    this.member.tmp.firstInvoice = value.firstInvoice;
    this.member.tmp.firstInvoiceDate = value.firstInvoiceDate;
    this.member.tmp.firstInvoiceTotal = value.total;
    this.member.isKeyDelivered = 1;
    return true;
  }

  private clearFirstInvoice(): void {
    this.quote.clear();
    this.member.tmp.firstInvoice = undefined;
    this.member.tmp.firstInvoiceDate = undefined;
    this.member.tmp.firstInvoiceTotal = undefined;
  }

  switchToEntry(): void {
    this.member.isEntry = 1;
    this.selectPlan = '';
    this.clearFirstInvoice();
    if (this.isUnmanned === 1) this.paymentMethod = '1';
  }

  switchToVisit(): void {
    this.member.desiredStartDate = undefined;
    this.clearFirstInvoice();
    this.member.isEntry = 0;
    this.selectPlan = '999999999,見学予約,0,0';
    this.paymentMethod = '0';
  }

  searchZipCode(zip1?: string, zip2?: string): void {
    if (!zip1 || !zip2) return;
    const zip = zip1 + zip2;
    this.member.zip = zip1 + '-' + zip2;
    this.sfapp.getAddress(zip).then((data) => {
      if (data) {
        this.member.province = data.address1;
        this.member.city = data.address2;
        this.member.area = data.address3;
      }
    });
  }

  checkPattern(value: string, regex: string): void {
    this.isOKCcName = new RegExp(regex).test(value) ? 'OK' : '';
  }

  toggleRadio(qkey: number, columnId: number): void {
    const q = this.questionarys[qkey];
    const inputMemoFlg = this.getInputMemoFlg(q.selectColumns, columnId);
    q.isActiveTextArea = !!inputMemoFlg;
    this.clearInputMemo(qkey);
  }

  toggleCheck(qkey: number, columnId: number): void {
    const q = this.questionarys[qkey];
    const isCheck = q.check?.[columnId];
    if (!q.selectColumns.value) q.selectColumns.value = [];
    if (isCheck) {
      q.selectColumns.value.push(columnId);
      q.selectColumns.value.sort((a: number, b: number) => a - b);
      if (this.getInputMemoFlg(q.selectColumns, columnId)) q.isActiveTextArea = true;
    } else {
      q.selectColumns.value = q.selectColumns.value.filter((v: number) => v !== columnId);
      if (this.getInputMemoFlg(q.selectColumns, columnId)) {
        this.clearInputMemo(qkey);
        q.isActiveTextArea = false;
      }
    }
    q.selectColumns.hasCheck = q.selectColumns.value.length > 0 ? true : undefined;
  }

  clearInputMemo(qkey: number): void {
    if (this.questionarys[qkey]) {
      this.questionarys[qkey].selectColumns.inputMemo = undefined;
    }
  }

  private getInputMemoFlg(columns: any, id: number): boolean {
    for (const key of Object.keys(columns)) {
      if (isNaN(Number(key))) continue;
      if (columns[key].id === id && columns[key].inputMemoFlg === 1) return true;
    }
    return false;
  }

  questionaryEntries(): [string, any][] {
    return Object.entries(this.questionarys);
  }

  columnEntries(selectColumns: any): [string, any][] {
    return Object.entries(selectColumns).filter(([k]) => k !== 'value' && k !== 'inputMemo' && k !== 'hasCheck');
  }

  agreementFor(doc: string): SafeHtml {
    return this.agreementHtml[`${doc}-${this.lang}`] ?? '';
  }

  async openVisitModal(): Promise<void> {
    if (!this.selectVisitPlan) return;
    await this.clearReserve();
    const menu = this.visitMenu.find((m) => m.id === Number(this.selectVisitPlan));
    this.member.reservationMenu = menu;
    const availability = await this.sfapp.getAvailabilityForReserve(
      this.member.studioId!, menu.timePerSlot, 1, 0
    );
    this.initCalendar(availability, menu);
    this.showVisitModal = true;
    // 旧版はBootstrapのdata-toggleが body.modal-open を付けていた。
    // v4はmodalを自前で組み立てているため明示的に付ける。
    // これが無いと `.modal { overflow: hidden }` のままでmodalが縦スクロールできず、背景のbodyが動いてしまう。
    document.body.classList.add('modal-open');
  }

  closeVisitModal(): void {
    this.showVisitModal = false;
    document.body.classList.remove('modal-open');
  }

  private initCalendar(events: any[], menu: any): void {
    this.calendarOptions = {
      timeZone: 'Asia/Tokyo', now: calendarNow,
      plugins: [jstCalendarPlugin, timeGridPlugin, interactionPlugin, googleCalendarPlugin],
      locale: 'ja-JP',
      contentHeight: 'auto',
      initialView: 'timeGridWeek',
      slotMinTime: '09:00:00',
      slotMaxTime: '21:15:00',
      slotEventOverlap: false,
      slotLabelInterval: '00:15:00',
      dayMaxEventRows: 3,
      eventDisplay: 'block',
      // 旧版 member/js/app.js:1006-1025 の書式指定。落ちていると locale ja-JP の既定書式になり、
      // 正時のslotラベルが「9:00」ではなく「9時」になる（omitZeroMinute の既定が true のため）
      titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' },
      slotDuration: '00:15:00',
      slotLabelFormat: { hour12: false, hour: '2-digit', minute: '2-digit', omitZeroMinute: false, meridiem: false },
      displayEventEnd: false,
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      eventMinHeight: 15,
      allDaySlot: false,
      headerToolbar: { end: 'today prev,next' },
      validRange: () => ({
        start: businessDayjs().format('YYYY-MM-DD'),
        end: businessDayjs().add(30, 'day').format('YYYY-MM-DD'),
      }),
      events,
      eventDataTransform: (eventData: any) => {
        if (eventData?.url) {
          return { ...eventData, allDay: false, start: businessDayjs(eventData.start).startOf('day').toISOString(), end: businessDayjs(eventData.start).endOf('day').toISOString() };
        }
        return { ...eventData, menu };
      },
      eventDidMount: (info: any) => {
        if (!info.event?._def?.url) {
          const color = info.event?.id ? '#4EBCC4' : '#FFDAB3';
          info.el.style.borderColor = color;
          info.el.style.backgroundColor = color;
        }
      },
      eventContent: (info: any) => ({
        html: `<div><span class="calender-time-text">${info.timeText}</span><div class="text-center"><span class="material-symbols-outlined" style="font-size:1rem">${info.event.title}</span></div></div>`,
      }),
      datesSet: () => {
        // datesSet の引数に el は無い。旧版 member/js/app.js:1079 と同じく document から引く
        const header = document.querySelector('.fc-header-toolbar');
        if (header && !header.querySelector('.fc-custom-notice')) {
          const notice = document.createElement('div');
          notice.className = 'fc-custom-notice';
          notice.textContent = '当日のご予約はお電話にてお問い合わせください。';
          notice.style.fontSize = '14px';
          notice.style.marginTop = '4px';
          header.appendChild(notice);
        }
      },
      eventClick: async (info: any) => {
        if (this.isReserveProcessing || !await this.businessClock.confirm(this.clockContext, () => this.refreshClockDates())) return;
        if (!info.event?.id || !businessDayjs(info.event.id).isAfter(businessDayjs().endOf('day')) || !businessDayjs(info.event.id).isBefore(businessDayjs().startOf('day').add(30, 'day'))) return;
        this.isReserveProcessing = true;
        if (info.event?.id) {
          const json = {
            startedAt: info.event._def.publicId,
            studioId: this.member.studioId,
            reservationMenuId: menu.id,
          };
          const result = await this.sfapp.createVisitReserve(json).catch(() => null);
          this.isReserveProcessing = false;
          if (result?.error) {
            this.toastr.error(result.error ?? '画面を更新します。再度見学希望日を選択してください。', 'エラー');
            const refreshed = await this.sfapp.getAvailabilityForReserve(
              this.member.studioId!, menu.timePerSlot, 1, 0
            );
            this.calendarOptions = { ...this.calendarOptions, events: refreshed };
          } else if (result) {
            const { data, expire } = result;
            this.member.reservation = data;
            this.member.reservationId = data?.map((d: any) => d?.id) ?? [];
            localStorage.setItem('reservationId', JSON.stringify(this.member.reservationId));
            this.visitDatetime = businessDayjs(data[0]?.startedAt).tz('Asia/Tokyo').format('YYYY/MM/DD(ddd) HH:mm');
            this.toastr.success(`見学希望日を「${this.visitDatetime}」に設定しました。${expire ? expire + '分間有効です。' : ''}`, '仮予約成功');
            this.closeVisitModal();
          } else {
            this.toastr.error('画面を更新して再度コースの選択を行ってください。', 'エラー');
          }
        } else {
          this.isReserveProcessing = false;
        }
      },
      eventSources: [{
        googleCalendarApiKey: 'AIzaSyBAVtelIrW2nNaefyxUPtSGHSWKmhcomY0',
        googleCalendarId: 'japanese__ja@holiday.calendar.google.com',
        display: 'background',
        color: '#ffeaea',
      }],
    };
  }

  async next(): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext, () => this.refreshClockDates())) return;
    if ((!this.member.isRegistration && this.member.email !== this.member.confirmEmail) || this.confirming || this.stepTwoForm?.pending || (this.requiresQuestionary && (!this.questionaryLoaded || this.questionaryLoading)) || this.stepTwoForm?.invalid) return;
    this.confirming = true; this.confirmationError = '';
    const source = this.state;
    try {
      if (this.selectPlan && Number(this.selectPlan.split(',')[0]) !== 999999999) {
        await this.clearReserve();
        if (!this.alive || source !== this.stateService.get()) return;
        this.member.reservationMenu = null; this.selectVisitPlan = ''; this.visitDatetime = ''; this.visitNote = '';
      }
      if (!(await this.loadFirstInvoice()) || !this.alive) return;
      const snapshot = confirmationInput(this.member, {
        plan: this.selectPlan, payment: this.paymentMethod, sex: this.selectSex, nationality: this.isJapanese,
        relationship: this.emRelationship, questionaries: this.questionarys, visitPlan: this.selectVisitPlan,
        visitDatetime: this.visitDatetime, visitNote: this.visitNote, cardNameValid: this.isOKCcName,
      });
      const duplicate = await this.registrationApi.emailExists(snapshot.email);
      if (!this.alive || source !== this.stateService.get()) return;
      this.invalidEmail = !!snapshot.email && duplicate;
      if (this.invalidEmail) return;
      if (!snapshot.reservationId?.length && this.paymentMethod === '1') {
        const token = await this.gmo.getToken(environment.shopId, snapshot.cc);
        if (!this.alive || source !== this.stateService.get()) return;
        if (token === 'error') { this.gmoTokenError = true; return; }
        this.gmoTokenError = false; snapshot.cc.token = token;
      }
      if (!await this.businessClock.confirm(this.clockContext, () => this.refreshClockDates())) return;
      this.state.member = snapshot;
      await this.router.navigate(['/step3', snapshot.tmp.studio![0], snapshot.tmp.studio![1].trim()]);
    } catch {
      if (this.alive) this.confirmationError = '入力内容の確認に失敗しました。入力は保持しています。もう一度お試しください。';
    } finally { if (this.alive) this.confirming = false; }
  }
  private refreshClockDates(): void {
    const d = businessDayjs();
    this.desiredStartDateList = getDesiredStartDateList([d.year(), d.month()+1, d.date(), d.hour()].map(String), d.day());
    this.member.desiredStartDate = undefined;
    this.birthYears = getYearList(d.year());
    this.ccExpireYears = getCcYearList(d.year());
  }

}
